import React, { useState, useEffect } from 'react';
import { Check, Package, Truck, Clock, XCircle, ShoppingBag, MessageCircle, ArrowLeft, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { tx } from '@/translations/translations';
import { displaySize } from '@/utils/displaySize';

const STATUS_STEPS = [
  { key: 'Nuevo', icon: Check, labelEs: 'Pedido recibido', labelEn: 'Order received', labelHe: 'ההזמנה התקבלה' },
  { key: 'En preparación', icon: Package, labelEs: 'En preparación', labelEn: 'Preparing', labelHe: 'בהכנה' },
  { key: 'Enviado', icon: Truck, labelEs: 'Enviado', labelEn: 'Shipped', labelHe: 'נשלח' },
  { key: 'Entregado', icon: Check, labelEs: 'Entregado', labelEn: 'Delivered', labelHe: 'נמסר' },
];

const OrderTracking = ({ orderId, onClose }) => {
  const { language } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        setOrder(data);
        setError(null);
      })
      .catch(() => setError(tx(language, 'Pedido no encontrado', 'Order not found', 'ההזמנה לא נמצאה')))
      .finally(() => setLoading(false));
  }, [orderId, language]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">{tx(language, 'Cargando pedido...', 'Loading order...', 'טוען הזמנה...')}</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
          <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <p className="text-base font-bold text-slate-800 mb-1">{error}</p>
          <p className="text-xs text-slate-500 mb-5">
            {tx(language, 'Verifica el número e inténtalo de nuevo', 'Please verify the number and try again', 'בדקו את המספר ונסו שוב')}
          </p>
          <Button onClick={onClose} className="bg-slate-900 text-white">
            {tx(language, 'Volver', 'Back', 'חזרה')}
          </Button>
        </div>
      </div>
    );
  }

  const isCancelled = order.order_status === 'Cancelado';
  const currentStepIdx = STATUS_STEPS.findIndex((s) => s.key === order.order_status);
  const activeIdx = currentStepIdx === -1 ? 0 : currentStepIdx;
  const idShort = String(order.id).slice(-8).toUpperCase();
  const total = order.total || 0;
  const currency = order.currency || 'MXN';
  const createdAt = order.created_at ? new Date(order.created_at).toLocaleDateString(language === 'es' ? 'es-MX' : language === 'he' ? 'he-IL' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const whatsappLink = `https://wa.me/525543233705?text=${encodeURIComponent(
    tx(language, 'Hola, quisiera información sobre mi pedido #', 'Hi, I have a question about order #', 'שלום, אשמח למידע על הזמנה #') + idShort
  )}`;

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] overflow-y-auto p-3 sm:p-4">
      <div className="min-h-full flex items-start justify-center pt-4 sm:pt-10">
        <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between">
            <button onClick={onClose} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="w-3.5 h-3.5" />
              {tx(language, 'Volver', 'Back', 'חזרה')}
            </button>
            <h2 className="font-bold text-sm">
              {tx(language, 'Pedido', 'Order', 'הזמנה')} #{idShort}
            </h2>
            <button
              onClick={() => {
                navigator.clipboard.writeText(idShort);
                toast.success(tx(language, '¡Copiado!', 'Copied!', 'הועתק!'));
              }}
              className="text-slate-400 hover:text-white"
              data-testid="copy-order-id"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          {/* Status Timeline */}
          <div className="p-5 sm:p-6">
            {isCancelled ? (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 flex items-center gap-3">
                <XCircle className="w-8 h-8 text-rose-500 flex-shrink-0" />
                <div>
                  <p className="font-bold text-rose-900 text-sm">
                    {tx(language, 'Pedido cancelado', 'Order cancelled', 'ההזמנה בוטלה')}
                  </p>
                  <p className="text-xs text-rose-700">
                    {tx(language, 'Contáctanos para más información', 'Contact us for more information', 'צרו קשר למידע נוסף')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="relative">
                {STATUS_STEPS.map((step, idx) => {
                  const Icon = step.icon;
                  const isDone = idx < activeIdx;
                  const isCurrent = idx === activeIdx;
                  return (
                    <div key={step.key} className="relative flex items-start gap-3 pb-6 last:pb-0">
                      {idx < STATUS_STEPS.length - 1 && (
                        <div
                          className={`absolute left-4 top-9 bottom-0 w-0.5 ${
                            isDone ? 'bg-cyan-500' : 'bg-slate-200'
                          }`}
                        />
                      )}
                      <div
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDone
                            ? 'bg-cyan-500 text-white'
                            : isCurrent
                            ? 'bg-cyan-500 text-white ring-4 ring-cyan-100'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="pt-1">
                        <p
                          className={`text-sm font-semibold ${
                            isDone || isCurrent ? 'text-slate-900' : 'text-slate-400'
                          }`}
                        >
                          {tx(language, step.labelEs, step.labelEn, step.labelHe)}
                        </p>
                        {isCurrent && (
                          <p className="text-xs text-cyan-600 font-medium mt-0.5">
                            {tx(language, 'Estado actual', 'Current status', 'סטטוס נוכחי')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            <div className="mt-5 rounded-xl bg-slate-50 p-4 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{tx(language, 'Fecha', 'Date', 'תאריך')}</span>
                <span className="font-medium text-slate-800">{createdAt}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{tx(language, 'Productos', 'Items', 'מוצרים')}</span>
                <span className="font-medium text-slate-800">
                  {(order.items || []).reduce((s, i) => s + (i.quantity || 0), 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{tx(language, 'Total', 'Total', 'סה״כ')}</span>
                <span className="font-bold text-slate-900 text-sm">${total.toFixed(2)} {currency}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{tx(language, 'Método', 'Method', 'אמצעי')}</span>
                <span className="font-medium text-slate-800 capitalize">{order.payment_method || '—'}</span>
              </div>
            </div>

            {/* Items */}
            {(order.items || []).length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                  {tx(language, 'Productos', 'Items', 'מוצרים')}
                </p>
                <div className="space-y-2">
                  {order.items.map((it, idx) => (
                    <div key={`${it.id}-${it.size}-${idx}`} className="flex items-center justify-between text-xs bg-white border border-slate-100 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{it.name}</p>
                        <p className="text-slate-400 mt-0.5">
                          {tx(language, 'Talla', 'Size', 'מידה')}: {displaySize(it.size, language)} · x{it.quantity}
                        </p>
                      </div>
                      <span className="font-semibold text-slate-700 ml-2">${(it.price * it.quantity).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-3 rounded-xl transition-colors"
              data-testid="contact-whatsapp-order"
            >
              <MessageCircle className="w-4 h-4" />
              {tx(language, '¿Necesitas ayuda? Chatea', 'Need help? Chat', 'צריכים עזרה? דברו איתנו')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderLookup = ({ onClose }) => {
  const { language } = useLanguage();
  const [input, setInput] = useState('');
  const [activeId, setActiveId] = useState(null);

  if (activeId) {
    return <OrderTracking orderId={activeId} onClose={() => setActiveId(null)} />;
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-cyan-600" />
            {tx(language, 'Rastrear Pedido', 'Track Order', 'מעקב הזמנה')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          {tx(language, 'Ingresa el ID de tu pedido (lo recibiste por email)', 'Enter your order ID (received via email)', 'הזינו את מזהה ההזמנה (קיבלתם באימייל)')}
        </p>
        <input
          autoFocus
          type="text"
          placeholder={tx(language, 'ID del pedido', 'Order ID', 'מזהה הזמנה')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-cyan-500 outline-none text-sm"
          data-testid="order-lookup-input"
        />
        <Button
          onClick={() => input.trim() && setActiveId(input.trim())}
          disabled={!input.trim()}
          className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="order-lookup-submit"
        >
          {tx(language, 'Ver estado', 'View status', 'צפייה בסטטוס')}
        </Button>
      </div>
    </div>
  );
};

export { OrderTracking, OrderLookup };
export default OrderTracking;
